<template>
  <div class="mt-2 form-responsive">
    <v-row no-gutters v-if="fieldsLoading">
      <v-col v-for="n in 9" :key="n" cols="12" sm="6" lg="3">
       <v-sheet class="mt-2">
        <v-skeleton-loader class="mx-auto" type="list-item"></v-skeleton-loader>
      </v-sheet>
      </v-col>
    </v-row>
    <form-data v-else :references.sync="formReferences" :model="modelObj" ref="drawingReference">
      <template v-if="modelObj.id && listOfDocuments.length > 0" slot="drawing_documents">
        <documents :documents="listOfDocuments" :deleteIds="deleteDocumentIds"></documents>
      </template>
    </form-data>
  </div>
</template>
<script>
import { mapGetters } from 'vuex'
import { hostAppApi } from '@/plugins/axios_settings'
import PermissionsMixin from '@/mixins/permissions'

export default {
  mixins: [PermissionsMixin],
  data () {
    return {
      statusValue: [
        { text: this.$t('message.common.ready_for_production'), value: 1 },
        { text: this.$t('message.common.production'), value: 2 },
        { text: this.$t('message.common.on_hold'), value: 3 },
        { text: this.$t('message.common.finished'), value: 4 },
        { text: this.$t('message.common.archived'), value: 5 }
      ],
      listOfProjects: [],
      drawingTypList: [],
      advanceDrawingTypes: null,
      modelObj: {},
      loading: false,
      projectId: parseInt(this.$route.params.project_id) || 0,
      fieldsLoading: false,
      hostUserId: this.$cookie.get(process.env.VUE_APP_HOST_USER) ? this.$cookie.get(process.env.VUE_APP_HOST_USER) : 0,
      backUrl: '',
      listOfDocuments: [],
      deleteDocumentIds: [],
      hideDetails: true
    }
  },
  computed: {
    ...mapGetters(['formType', 'generalSettings', 'userDetails', 'getHostRefApi']),
    ...mapGetters('project', ['project']),
    formReferences () {
      /* One place to change a width, instead of six copies of the same string.
         phone (xs) / tablet portrait (sm) / tablet landscape (md) / desktop (lg) */
      const col = {
        heading: 'xs12',
        code: 'xs12 sm6 md4 lg3', // short identification fields
        wide: 'xs12', // description
        status: 'xs12 sm6 md4 lg3',
        files: 'xs12 sm12 md8 lg6'
      }
      const isNew = !this.modelObj.id

      return {
        cardFlat: true,
        cardTitleClass: 'pa-1',
        layoutClass: 'pa-1',
        title: isNew ? this.$t('message.common.addNew') : this.$t('message.common.update'),
        properties: [
          /* ---------------- Identification ---------------- */
          {
            type: 'section',
            label: this.$t('message.projects.sectionIdentification'),
            class: col.heading
          }, {
            model: 'drawning_nr',
            type: this.formType.TEXT,
            rules: this.$_requiredValidation,
            label: this.$t('message.projectFeature.drawingNumber'),
            class: col.code
          }, {
            model: 'drawing_type_id',
            type: this.formType.AUTO_COMPLETE,
            items: this.drawingTypList,
            select_text: 'name',
            select_value: 'id',
            is_show: this.advanceDrawingTypes,
            label: this.$t('message.projects.drawingGroup'),
            /* Only required when actually shown; a hidden required field blocks saving. */
            rules: this.advanceDrawingTypes ? this.$_requiredValidation : [],
            class: col.code
          }, {
            model: 'po_ranked',
            type: this.formType.TEXT,
            readonly: true,
            label: this.$t('message.projectFeature.poRanked'),
            class: col.code
          },

          /* ---------------- Description ---------------- */
          {
            type: 'section',
            label: this.$t('message.deviation.description'),
            class: col.heading
          }, {
            model: 'description',
            type: this.formType.TEXTAREA,
            rules: [],
            label: this.$t('message.deviation.description'),
            class: col.wide,
            /* user-resizable via − / + (persisted per route); starts at 3 rows,
               capped at 15 so the sticky Save/Back bar stays visible on a 768px tablet */
            rows: 3,
            resizable: true,
            minRows: 2,
            maxRows: 15
          },

          /* ---------------- Status and attachments ---------------- */
          {
            type: 'section',
            label: this.$t('message.projects.sectionStatusAttachments'),
            class: col.heading
          }, {
            model: 'is_completed',
            type: this.formType.SELECT,
            items: this.statusValue,
            rules: this.$_requiredValidation,
            label: this.$t('message.projects.status'),
            class: col.status
          }, {
            model: 'files',
            type: this.formType.FILES,
            multiple: true,
            extraExtensions: ['mht'],
            rules: this.$_maxFileSizeValidation(100, this.modelObj.files),
            label: this.$t('message.customModule.uploadDocument'),
            class: col.files,
            slot: 'drawing_documents'
          }
        ],
        buttons: [{
          name: 'save_drawing',
          color: 'primary',
          label: isNew ? this.$t('message.common.save') : this.$t('message.common.update'),
          click: this.saveUpdateHandler,
          loading: this.loading,
          is_show: true
        }, {
          name: 'back',
          color: 'info',
          click: () => this.$router.go(-1),
          label: this.$t('message.login.back'),
          to: this.backUrl,
          is_show: true
        }]
      }
    }
  },
  mounted () {
    this.getDrawingTypes()

    // set back url
    this.backUrl = this.projectId ? `/projects/${this.projectId}/drawing` : '/drawing'
    this.initializeFormData()
  },
  components: {
    'form-data': () => import('@/components/FormTemplate'),
    documents: () => import('@/components/Documents/Documents')
  },
  methods: {
    initializeFormData () {
      if (!this.project) return
      this.fieldsLoading = true
      this.modelObj = {
        po_ranked: this.project.number,
        project_id: this.projectId,
        is_completed: this.statusValue[0].value
      }
      if (this.$route.params.id) {
        this.initializeEditData(this.$route.params.id)
        return
      }
      this.fieldsLoading = false
    },
    saveUpdateHandler () {
      if (this.$refs.drawingReference.$refs.validateForm.validate()) {
        this.loading = true
        const files = this.modelObj.files
        const model = this.$formatter.cloneVariable(this.modelObj)
        model.user_id = parseInt(this.hostUserId)
        const formData = new FormData()
        if (files) {
          const Newfile = [...files]
          Newfile.forEach((file, i) => {
            formData.append('document[' + i + ']', file)
          })
        }
        const newobj = this.$formatter.removeInvalidFromObject(model)
        setTimeout(() => {
          for (const key in newobj) {
            if (key !== 'files') formData.append(key, newobj[key] ?? '')
          }
          const header = {
            'Content-Type': 'multipart/form-data'
          }
          let url = this.getHostRefApi
          // const method = model.id ? 'put' : 'post'
          url += model.id ? `project_drawing/${model.id}/update` : 'project_drawing'
          const text = model.id ? 'message.common.updatedSuccess' : 'message.common.addedSucess'
          /* on update check if there is documents in array to delete */
          const ids = [...this.deleteDocumentIds]
          if (model.id && ids.length > 0) formData.append('document_delete_ids', ids)
          hostAppApi.post(url, formData, { headers: header }) // `${url}?locale=${this.$i18n.locale}`
            .then((response) => {
              this.$root.$emit('snackbar', { snackbar: true, color: 'success', text })
              this.$router.push(this.backUrl)
            })
            // .catch(error => {
            //   if (error.response) {
            //     let exception = error.response
            //     if (error.response.data) exception = `Status Code : ${error.response.status}, Message : ${error.response.data}`
            //     else exception = `Status Code : ${error.response.status}, Message : ${error.response.statusText}`
            //     this.$root.$emit('snackbar', { snackbar: true, color: 'error', text: JSON.stringify(exception), isException: true })
            //     if (error.response.status === 401) {
            //       this.$root.$emit('snackbar', { snackbar: true, color: 'error', text: 'message.common.authFailed' })
            //       this.$store.dispatch('logout')
            //       this.$router.push('/login').catch(() => {})
            //     }
            //   } else {
            //     this.$root.$emit('snackbar', { snackbar: true, color: 'error', text: JSON.stringify(error.message), isException: true })
            //   }
            // })
            .finally(() => {
              this.loading = false
            })
        }, 50)
      } else this.$root.$emit('snackbar', { snackbar: true, color: 'error', text: 'message.common.validationIssue' })
    },
    initializeEditData (id) {
      this.fieldsLoading = true
      hostAppApi.get(`${this.getHostRefApi}project_drawing/${id}/edit?user_id=${this.hostUserId}&project_id=${this.projectId}`)
        .then((response) => {
          if (response.data) {
            this.modelObj = {
              ...this.modelObj,
              ...response.data
            }
            this.listOfDocuments = this.$formatter.removeInvalidFromArrayObject(this.modelObj.documents)
          }
        })
        .finally(() => {
          this.fieldsLoading = false
        })
    },
    getDrawingTypes () {
      hostAppApi.get(`${this.getHostRefApi}project_drawing/create?user_id=${this.hostUserId}&project_id=${this.projectId}`)
        .then((response) => {
          if (response && response.data && response.data.drawing_types) {
            this.drawingTypList = response.data.drawing_types
          }
          if (response.data && response.data.advance_drawing) {
            this.advanceDrawingTypes = true
          } else {
            this.advanceDrawingTypes = false
          }
        })
    }
  },
  watch: {
    isShowDocumentErrors (val) {
      this.hideDetails = !val
    }
  }
}
</script>

<!-- Layout rules for this form live in the shared .form-responsive block in src/assets/css/app.scss. -->
